import { Injectable } from '@angular/core';
import { HTTP } from '@awesome-cordova-plugins/http/ngx';
import { SQLite, SQLiteObject } from '@awesome-cordova-plugins/sqlite/ngx';
import { ApiService } from 'src/app/api.service';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { ExtractHtmlImgService } from './extract-html-img.service';
import { AppPlatformService } from '../platform/app-platform.service';
import { UsuarioServiceProvider } from '../index.providers';
import { BehaviorSubject } from 'rxjs';
export interface DownloadProgress {
  percentage: number;
  currentStep: string;
  completed: boolean;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChecklistOfflineService {
  private database: SQLiteObject;
  private allDownloadedChecklist = [];
  private downloadedChecklistID = [];

  private allDownloadedEntities = [];

  private allDownloadedThemes = [];
  private allDownloadedThemesGroupedByArea = [];

  private allDownloadedActivities = [];
  private allDownloadedMainAudits = [];

  // Subject para el progreso de descarga
  private downloadProgressSubject = new BehaviorSubject<DownloadProgress>({
    percentage: 0,
    currentStep: '',
    completed: false
  });

  // Observable público para que los componentes se suscriban
  public downloadProgress$ = this.downloadProgressSubject.asObservable();

  constructor(
    private sqlite: SQLite,
    private api: ApiService,
    private file: File,
    private nativeHTTP: HTTP,
    private extractHtmlImgService: ExtractHtmlImgService,
    private appPlatformService:AppPlatformService,
    private up: UsuarioServiceProvider,

  ) {
    if (this.appPlatformService.isAppPlatform()) {
        this.createSQLdb();
    };

    this.up.isLogged.subscribe((isLogged) => {
			if (!isLogged) {
        this.database = null;
			};
		});
  }

  get getDatabaseRef() {
      return this.database;
  }

  get getDownloadsID() {
		return  this.downloadedChecklistID;
	}

  // Método para actualizar el progreso
  private updateProgress(percentage: number, currentStep: string, completed: boolean = false, error?: string) {
    this.downloadProgressSubject.next({
      percentage: Math.round(percentage),
      currentStep,
      completed,
      error
    });
  }
  // Método auxiliar para calcular el número total de pasos
  private calculateTotalSteps(data: any, pendingEntities: any[]): number {
    let totalSteps = 4; // Pasos básicos: obtener datos, checklist, entidades, actividades
    if (data.checklist?.has_themes) {
      const noStartedEntities = pendingEntities.filter(entity => entity.status === "no-started");
      const inProgressEntities = pendingEntities.filter(entity => entity.status === "in-progress");
      totalSteps += noStartedEntities.length + inProgressEntities.length;
    };
    // Un paso por cada entidad para las auditorías principales
    totalSteps += pendingEntities.length;
    // Un paso adicional para la tabla de auditorías offline
    totalSteps += 1;
    return totalSteps;
  }

  resetProgress() {
    this.updateProgress(0, '', false);
  }

  async downloadAllChecklistData(checklistID:number) {
    try {
        // Reset progreso
        this.updateProgress(0, 'Iniciando descarga...');

        const {data} = await this.api.getChecklistRawData(checklistID);
        console.log("all checklist data de api:", data);
        this.updateProgress(10, 'Datos obtenidos de la API');

        let pendingEntities;
        if(data.checklist.auditor_calificate_all_entity) {
          pendingEntities = data.entities.filter(entity => entity.status === "no-started" || entity.status === "in-progress");
        } else {
          pendingEntities = data.entities
        }
        if(pendingEntities?.length) {
           // Calcular el número total de pasos para el progreso
          const totalSteps = this.calculateTotalSteps(data, pendingEntities);
          let completedSteps = 1; // Ya completamos la obtención de datos
           this.updateProgress((completedSteps / totalSteps) * 100, 'Creando tabla de checklist...');

          // checklist
          await this.createChecklistTable(data.checklist, data.checklist.imagen, data.checklist.auditor_calificate_all_entity ? false : true );
          completedSteps++;

          // tematicas
          let listOriginalAreasAndThemes = [];
          let allOriginalThemes = [];

          if (data.checklist?.has_themes) {
             this.updateProgress( (completedSteps / totalSteps) * 100, 'Procesando temas y áreas...');
            // Primero obtenemos todos los temas y áreas para no-started
            const noStartedEntities = pendingEntities.filter(entity => entity.status === "no-started");
            if (noStartedEntities?.length > 0) {
              // Solo obtenemos los temas una vez para todas las entidades no-started
              const firstEntityId = noStartedEntities[0].id;
              let res: any = await this.api.obtenerListaTemasYAreas(checklistID, null, null, firstEntityId);
              listOriginalAreasAndThemes = res.data;

              allOriginalThemes = listOriginalAreasAndThemes.flatMap(area =>
                area.themes.map(theme => ({
                  ...theme,
                  area_id: area.id,
                  area_name: area.name
                }))
              );
              console.log("allOriginalThemes:: ", allOriginalThemes);

              // Procesamos todas las entidades no-started
              for (const entity of noStartedEntities) {
                this.updateProgress( (completedSteps / totalSteps) * 100, `Procesando temas para ${noStartedEntities.length} entidades (no iniciada)`);
                entity.list_themes = [];
                entity.themeIds = [listOriginalAreasAndThemes[0]?.themes[0]?.id];
                // Usamos await para cada entidad para garantizar que termine antes de pasar a la siguiente
                await this.createThemesTable(allOriginalThemes, entity.id, data.checklist.id);
                completedSteps++;
              }
            }

            // Procesamos todas las entidades in-progress una por una
            const inProgressEntities = pendingEntities.filter(entity => entity.status === "in-progress");
            if (inProgressEntities?.length > 0){
              for (const entity of inProgressEntities) {
                this.updateProgress( (completedSteps / totalSteps) * 100, `Procesando temas para ${inProgressEntities.length} entidades (en progreso)`);

                let res: any = await this.api.obtenerListaTemasYAreas(checklistID, null, null, entity.id);
                const listAreasAndThemesInProgress = res.data;
                entity.list_themes = [];
                entity.themeIds = [listAreasAndThemesInProgress[0]?.themes[0]?.id];

                const allThemes = listAreasAndThemesInProgress.flatMap(area =>
                  area.themes.map(theme => ({
                    ...theme,
                    area_id: area.id,
                    area_name: area.name
                  }))
                );

                // Usamos await para cada entidad para garantizar que termine antes de pasar a la siguiente
                await this.createThemesTable(allThemes, entity.id, data.checklist.id);
                completedSteps++;
              }
            }

          };

          console.log(data.checklist.id," *************pendingEntities to download: ",pendingEntities)
          this.updateProgress((completedSteps / totalSteps) * 100, 'Creando tabla de entidades...');
          // entities
          await this.createEntitiesTable(pendingEntities, data.checklist.id);
          completedSteps++;

          const supervisedListRequired = data.checklist.required_signature_supervised;
          // main audits activities - el avance de actividades
          for (const entity of pendingEntities) {
            this.updateProgress((completedSteps / totalSteps) * 100, `Procesando auditorías para entidad ${entity.id}-${pendingEntities.length}`);

            await this.createMainAuditsTable(entity.audits, data.checklist.id, entity.id);
            //lista de supervisados
            if(supervisedListRequired) {
              this.updateProgress((completedSteps / totalSteps) * 100, `Obteniendo lista de supervisados para entidad ${entity.id}-${pendingEntities.length}` );

              let res: any = await this.api.getListSupervised(checklistID, entity.id, 1, "");
              const listSupervised = res.data.supervised;
              await this.createSupervisedTables(listSupervised, checklistID, entity.id);
            };
            completedSteps++;
            // await this.createOfflineAuditsToSyncChecklistTable(pendingEntities, data.checklist.id);
          };

          this.updateProgress((completedSteps / totalSteps) * 100,'Creando tabla de auditorías offline...');
          // Crear tabla de auditorías offline
          await this.createOfflineAuditsToSyncChecklistTable(pendingEntities, data.checklist.id);
          completedSteps++;
          // Crear tabla de actividades
          this.updateProgress((completedSteps / totalSteps) * 100, 'Creando tabla de actividades...');
          // activities
          await this.createActivitiesTable(data.activities, data.checklist.id);
          completedSteps++;

          // Descarga completada
          this.updateProgress(100, 'Descarga completada exitosamente', true);

        } else {
          this.updateProgress(100, 'No hay entidades pendientes para descargar', true);
          //alert
        };

    } catch (error) {
      this.updateProgress(0, '', false);
      // this.updateProgress(this.downloadProgressSubject.value.percentage, 'Error en la descarga', false, error.message || 'Error desconocido');
      throw error
    };
  }

  async createSQLdb() {
		//  "offlineCourses.db"
		try {
			const userData = JSON.parse(localStorage.getItem("user_data"));
			const db = await this.sqlite.create({
				name:`${userData.dni}.db`,
				location: "default"
			});
			console.log('** checklist Database opened: ', db);
			if(db) {this.database = db};
		} catch (error) {
      console.error('** checklist Error opening database', error);
      // throw error
		}
	}
  //*CHECKLIST
  async createChecklistTable(checklist, coverImg, noEntities) {
		if(!this.database) {
			await this.createSQLdb();
		};

    let imagen = await this.downloadChecklistCover(checklist, coverImg);
    console.log("**path desde dispositivo::: ",imagen);
    try {
      //! (entidad)pendiente el valor de: show_modal_action_plan
      await this.database.executeSql(`CREATE TABLE IF NOT EXISTS checklists (
        id integer primary key,
        auditor_calificate_all_entity integer,
        description text,
        modality text,
        title text,
        type text,

        entities_assigned integer,
        entities_finished integer,
        imagen text,
        percent_progress integer,
        required_geolocalization integer,

        has_knockout integer,
        has_themes integer,
        is_inverso integer,
        required_action_plan integer,
        required_signature_supervised integer,
        required_signature_supervisor integer,
        supervisor text,

        no_entities integer,
        entity_id integer
        )`,[]);


      const query = "INSERT OR REPLACE INTO checklists VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)";

      await this.database.executeSql(query, [
        checklist.id,
        checklist.auditor_calificate_all_entity ? 1 : 0,
        checklist.description,
        checklist.modality ? JSON.stringify(checklist.modality) : '',
        checklist.title,
        checklist.type ? JSON.stringify(checklist.type) : '',
        checklist.entities_assigned ,
        checklist.entities_finished ,
        imagen,
        checklist.percent_progress ,
        checklist.required_geolocalization ? 1 : 0,
        checklist.has_knockout ? 1 : 0,
        checklist.has_themes ? 1 : 0,
        checklist.is_inverso ? 1 : 0,
        checklist.required_action_plan ? 1 : 0,
        checklist.required_signature_supervised ? 1 : 0,
        checklist.required_signature_supervisor ? 1 : 0,
        checklist.supervisor,

        noEntities ? 1 : 0,
        checklist.entity_id
      ]);

      this.downloadedChecklistID.push(checklist.id);
    } catch (error) {
      console.log('Error createChecklistTable SQLite:', error);
       throw error
    };
  }

  async getOfflineChecklists() {
    if(!this.database) {
      await this.createSQLdb();
    };

   this.allDownloadedChecklist = [];
   let rawChecklist = []

    try {
      const data = await this.database.executeSql("SELECT * FROM checklists", []);
      if(data.rows.length > 0) {
        for(let i = 0; i < data.rows.length; i++) {
          rawChecklist.push(data.rows.item(i));
        };
      };

      rawChecklist.forEach(checklist => {
        checklist.modality = checklist.modality ? JSON.parse(checklist.modality) : '';
        checklist.type = checklist.type ? JSON.parse(checklist.type) : '';
        // checklist.entity = checklist.entity ? JSON.parse(checklist.entity) : '';
        // checklist.list_themes = checklist.list_themes ? JSON.parse(checklist.list_themes) : '';
        // checklist.themeIds = checklist.themeIds ? JSON.parse(checklist.themeIds) : '';
      });

      this.allDownloadedChecklist = rawChecklist;
      console.log("getOfflineChecklists::",this.allDownloadedChecklist)
      return this.allDownloadedChecklist;
    }
    catch (error) {
      console.log("Error getOfflineChecklists: " + JSON.stringify(error));
      return [];
    };
  }

  async getDownloadedChecklistByID(id, entity_id?) {
    let checklist = this.allDownloadedChecklist.find(ch => ch.id == id);
     if(checklist) {
      return checklist;
    };

    if(entity_id) {
      let rawChecklist = []

      try {
        const data = await this.database.executeSql("SELECT * FROM checklists WHERE id = ? AND entity_id = ?", [id, entity_id]);
        if(data.rows.length > 0) {
          for(let i = 0; i < data.rows.length; i++) {
            rawChecklist.push(data.rows.item(i));
          };
        };

        rawChecklist.forEach( checklist => {
          checklist.modality = checklist.modality ? JSON.parse(checklist.modality) : '';
          checklist.type = checklist.type ? JSON.parse(checklist.type) : '';
        });
        checklist = rawChecklist[0];
        return checklist
      }
      catch (error) {
        console.log("Error getOfflinechecklistByID: " + JSON.stringify(error));
        return null
      };

    }



	}

  async updateChecklistProgress(checklistId: number, activitiesReviewed: number, percentProgress: number) {
    // if (!this.database) { await this.createSQLdb() };
    // try {
    //   const query = `UPDATE checklists SET
    //   activities_reviewved = ?,
    //   percent_progress = ?

    //   WHERE id = ?`;

    //   await this.database.executeSql(query, [activitiesReviewed, percentProgress, checklistId]);
    //   console.log('checklists progress updated successfully!');
    //   return true;
    // } catch (error) {
    //   console.log('Error updating checklists progress:', error);
    //   throw error;
    // }
  }
  //*ENTITIES
  async createEntitiesTable(entitiesArray, checklistId) {
    console.log("createEntitiesTable array recibido:",entitiesArray)
    if(!this.database) { await this.createSQLdb() };
      //!  is_reopen , in_knockout
      // data_inverso text, no existe en modo offline
    try {
      await this.database.executeSql(`CREATE TABLE IF NOT EXISTS entities (
        id_code text primary key,
        id integer,
        content_modal text,
        finished integer,
        grade integer,
        hasAttempts integer,
        is_reopen integer,
        name text,
        status text,
        activities_assigned integer,
        activities_reviewved integer,
        in_knockout integer,
        percent_progress integer,
        list_themes text,
        themeIds text,
        checklistId integer
      )`,[]);

      const query = "INSERT OR REPLACE INTO entities VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)";

      for(let entity of entitiesArray) {
        await this.database.executeSql(query, [
          `${entity.id}-${checklistId}`,
          entity.id,
          entity.content_modal,
          entity.finished ? 1 : 0,
          entity.grade,
          0, //forzando solo 1 intento
          entity.is_reopen ? 1 : 0,
          entity.name,
          entity.status,
          entity.activities_assigned,
          entity.activities_reviewved,
          entity.in_knockout ? 1 : 0,
          entity.percent_progress,
          entity.list_themes ? JSON.stringify(entity.list_themes) : '', // [] obtenido de su propia api
          entity.themeIds ? JSON.stringify(entity.themeIds) : '', // [] obtenido de su propia api
          checklistId
        ])
        console.log('INSERT entities table!')
      };

    } catch (error) {
      console.log('Error createEntitiesTable SQLite:', error);
       throw error
    };
  }

  async getOfflineEntities(checklistId) {
    if(!this.database) {
      await this.createSQLdb();
    };

   this.allDownloadedEntities = [];

    try {
      const data = await this.database.executeSql("SELECT * FROM entities WHERE checklistId = ?", [checklistId]);
      if(data.rows.length > 0) {
        for(let i = 0; i < data.rows.length; i++) {
          this.allDownloadedEntities.push(data.rows.item(i));
        };
      };

      this.allDownloadedEntities.forEach( entity => {
        entity.list_themes = entity.list_themes ? JSON.parse(entity.list_themes) : '';
        entity.themeIds = entity.themeIds ? JSON.parse(entity.themeIds) : '';
      });

      return this.allDownloadedEntities;
    }
    catch (error) {
      console.log("Error getOfflineEntities: " + JSON.stringify(error));
      return this.allDownloadedEntities;
    };
  }

  async getOfflineSyncEntitiesIdCodes(checklistId) {
    if(!this.database) {
      await this.createSQLdb();
    };

   const allIdCodes = [];

    try {
      const data = await this.database.executeSql("SELECT entityId FROM offline_checklist_audits WHERE checklistId = ? AND has_data_to_sync = 1 AND is_synced = 0 AND sync_status != ?", [checklistId, 'synced']);
      if(data.rows.length > 0) {
        for(let i = 0; i < data.rows.length; i++) {
          allIdCodes.push(data.rows.item(i).entityId);
        };
      };

      return allIdCodes;
    }
    catch (error) {
      console.log("Error getOfflineSyncEntitiesIdCodes: " + JSON.stringify(error));
      return allIdCodes;
    };
  }
  async getOfflineSyncChecklistIdCodes() {
    if(!this.database) {
      await this.createSQLdb();
    };

   const allIdCodes = [];

    try {
      const data = await this.database.executeSql("SELECT checklistId FROM offline_checklist_audits WHERE has_data_to_sync = 1 AND is_synced = 0 AND sync_status != ?", ['synced']);
      if(data.rows.length > 0) {
        for(let i = 0; i < data.rows.length; i++) {
          allIdCodes.push(data.rows.item(i).checklistId);
        };
      };

      return allIdCodes;
    }
    catch (error) {
      console.log("Error getOfflineSyncChecklistId: " + JSON.stringify(error));
      return allIdCodes;
    };
  }

  async getDownloadedEntityByID(checklistId, entityId) {
    let entity =  this.allDownloadedEntities.find(entity => entity.id_code === `${entityId}-${checklistId}`);
    if(entity) {
      return entity;
    };

    let rawEntity = []

    try {
      const data = await this.database.executeSql("SELECT * FROM entities WHERE id_code = ? AND checklistId = ?", [ `${entityId}-${checklistId}`, checklistId]);
      if(data.rows.length > 0) {
        for(let i = 0; i < data.rows.length; i++) {
          rawEntity.push(data.rows.item(i));
        };
      };

      rawEntity.forEach( entity => {
        entity.list_themes = entity.list_themes ? JSON.parse(entity.list_themes) : '';
        entity.themeIds = entity.themeIds ? JSON.parse(entity.themeIds) : '';
      });
      entity = rawEntity[0];
      return entity
    }
    catch (error) {
      console.log("Error getOfflineEntities: " + JSON.stringify(error));
      return null
    };

	}
  async getFreshDownloadedEntityByID(checklistId, entityId) {
    let entity;
    let rawEntity = []

    try {
      const data = await this.database.executeSql("SELECT * FROM entities WHERE id_code = ? AND checklistId = ?", [ `${entityId}-${checklistId}`, checklistId]);
      if(data.rows.length > 0) {
        for(let i = 0; i < data.rows.length; i++) {
          rawEntity.push(data.rows.item(i));
        };
      };

      rawEntity.forEach( entity => {
        entity.list_themes = entity.list_themes ? JSON.parse(entity.list_themes) : '';
        entity.themeIds = entity.themeIds ? JSON.parse(entity.themeIds) : '';
      });
      entity = rawEntity[0];
      return entity
    }
    catch (error) {
      console.log("Error getOfflineEntities: " + JSON.stringify(error));
      return null
    };

	}

  async updateEntityProgress(checklistId: number, entityId: number, activitiesReviewed: number, percentProgress: number) {
    if (!this.database) { await this.createSQLdb() };
    try {
      const query = `UPDATE entities SET
      activities_reviewved = ?,
      percent_progress = ?

      WHERE id = ? AND checklistId = ?`;

      await this.database.executeSql(query, [activitiesReviewed, percentProgress, entityId, checklistId]);
      console.log('Entity progress updated successfully!');
      return true;
    } catch (error) {
      console.log('Error updating entity progress:', error);
      throw error;
    }
  }
  async updateEntityFinishStatus(checklistId: number, entityId: number) {
    if (!this.database) { await this.createSQLdb() };
    try {
      const query = `UPDATE entities SET
      finished = ?,
      status = ?

      WHERE id = ? AND checklistId = ?`;

      await this.database.executeSql(query, [1, "finished", entityId, checklistId]);

      const queryC = `UPDATE offline_checklist_audits SET
      finishes_at = ?,
      sync_status = ?

      WHERE checklistId = ? AND entityId = ?`;

      await this.database.executeSql(queryC, [this.formatDate(), "pending", checklistId, entityId]);
      console.log('Entity & offline_checklist_audits finishes_at updated successfully!');
      return true;
    } catch (error) {
      console.log('Error updating entity finished status:', error);
      throw error;
    }
  }

  async updateEntityKnockoutStatus(checklistId: number, entityId: number) {
    if (!this.database) { await this.createSQLdb() };
    try {
      const query = `UPDATE entities SET
      in_knockout = ?

      WHERE id = ? AND checklistId = ?`;

      await this.database.executeSql(query, [1, entityId, checklistId]);
      console.log('Entity in_knockout status updated successfully!');
      return true;
    } catch (error) {
      console.log('Error updating entity in_knockout status:', error);
      throw error;
    }
  }

  //*ACTIVITIES
  async createActivitiesTable(activitiesArray, checklistId) {
    if(!this.database) {
			await this.createSQLdb();
		}
    try {
      await this.database.executeSql(`CREATE TABLE IF NOT EXISTS activities (
        id integer primary key,
        area text,
        tematica_id integer,
        can_comment integer,
        can_computational_vision integer,
        can_upload_image integer,
        can_upload_video integer,
        checklist_type_response_inverso text,
        comments text,
        description text,
        has_knockout integer,
        knockout_selected integer,
        name text,
        photo text,
        principal_comment text,
        qualification_id integer,
        qualification_response text,
        system_calification text,
        type_system_calification text,
        checklistId integer
      )`,[]);

      const query = "INSERT OR REPLACE INTO activities VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)";

      for(let activity of activitiesArray) {
        if(activity.description) {
           const processedDescription = await this.extractHtmlImgService.processHtmlImages(activity.description);
           activity.description = processedDescription;
        };

        await this.database.executeSql(query, [
          activity.id,
          activity.area ? JSON.stringify(activity.area) : activity.area,
          activity.tematica_id ? activity.tematica_id : null,
          activity.can_comment ? 1 : 0,
          activity.can_computational_vision ? 1 : 0,
          activity.can_upload_image ? 1 : 0,
          activity.can_upload_video ? 1 : 0,
          activity.checklist_type_response_inverso ? JSON.stringify(activity.checklist_type_response_inverso) : activity.checklist_type_response_inverso,
          activity.comments ? JSON.stringify(activity.comments) : activity.comments,
          activity.description,
          activity.has_knockout  ? 1 : 0,
          activity.knockout_selected  ? 1 : 0,
          activity.name,
          activity.photo ? JSON.stringify(activity.photo) : activity.photo,
          activity.principal_comment ? JSON.stringify(activity.principal_comment) : activity.principal_comment,
          null, // valor inicial de qualification_id
          activity.qualification_response,
          activity.system_calification ? JSON.stringify(activity.system_calification) : activity.system_calification,
          activity.type_system_calification,
          checklistId
        ]
        );
        console.log('INSERT activities table!')
      };

    } catch (error) {
      console.log('Error createActivitiesTable SQLite:', error);
      throw error
    };
  }

  async getOfflineOriginalActivities(checklistId, themesIdListFilter = null) {
    if(!this.database) {
      await this.createSQLdb();
    };

   this.allDownloadedActivities = [];

    try {
      let data;
      if(themesIdListFilter && themesIdListFilter.length > 0) {
        // como obtener ?
        // Crear placeholders para la cantidad de elementos en themesIdListFilter (?, ?, ?, etc.)
        const placeholders = themesIdListFilter.map(() => '?').join(',');

        // Construir la consulta con IN para filtrar por theme_id
        const query = `SELECT * FROM activities WHERE checklistId = ? AND tematica_id IN (${placeholders})`;

        // Crear array de parámetros: primero checklistId, luego todos los theme_ids
        const params = [checklistId, ...themesIdListFilter];
        console.log("@@@@@@@ consulta IN para filtrar por theme_id: ", params)

        // Ejecutar consulta con los parámetros
        data = await this.database.executeSql(query, params);
      } else {
        data = await this.database.executeSql("SELECT * FROM activities WHERE checklistId = ?", [checklistId]);
      };
      if(data.rows.length > 0) {
        for(let i = 0; i < data.rows.length; i++) {
          this.allDownloadedActivities.push(data.rows.item(i));
        };
      };

      this.allDownloadedActivities.forEach(activity => {
        activity.area = activity.area ? JSON.parse(activity.area) : '';
        activity.checklist_type_response_inverso = activity.checklist_type_response_inverso ? JSON.parse(activity.checklist_type_response_inverso) : '';
        activity.comments = activity.comments ? JSON.parse(activity.comments) : [];
        activity.photo = activity.photo ? JSON.parse(activity.photo) : [];
        activity.principal_comment = activity.principal_comment ? JSON.parse(activity.principal_comment) : '';
        activity.system_calification = activity.system_calification ? JSON.parse(activity.system_calification) : [];
      });

      return this.allDownloadedActivities;
    }
    catch (error) {
      console.log("Error getOfflineOriginalActivities: " + JSON.stringify(error));
      return this.allDownloadedActivities;
    };
  }
  //*THEMES
  async createThemesTable(themesArray, entityId, checklistId) {
    if(!this.database) {
			await this.createSQLdb();
		}
    try {
      await this.database.executeSql(`CREATE TABLE IF NOT EXISTS themes (
        id_code text primary key,
        id integer,
        name text,
        finished integer,
        count_activities integer,
        count_activities_finished integer,
        area_id integer,
        area_name text,
        checklistId integer,
        entityId integer
      )`,[]);

      const query = "INSERT OR REPLACE INTO themes VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)";

      for(let theme of themesArray) {
        await this.database.executeSql(query, [
          `${entityId}-${checklistId}-${theme.id}`,
          theme.id,
          theme.name,
          theme.finished ? 1 : 0,
          theme.count_activities,
          theme.count_activities_finished,
          theme.area_id,
          theme.area_name,
          checklistId,
          entityId
        ])
        console.log('themes INSERT!')
      };

    } catch (error) {
      console.log('Error createThemesTable or INSERT SQLite:', error);
      throw error
    };
  }

  async getOfflineThemes(entityId, checklistId) {
    if(!this.database) {
      await this.createSQLdb();
    };

   this.allDownloadedThemes = [];

    try {
      // const themeCodeID = `${entityId}-${checklistId}`;
      const data = await this.database.executeSql("SELECT * FROM themes WHERE entityId = ? AND checklistId = ?", [ entityId, checklistId]);
      if(data.rows.length > 0) {
        for(let i = 0; i < data.rows.length; i++) {
          this.allDownloadedThemes.push(data.rows.item(i));
        };
      };

      const areasMap = {};
      this.allDownloadedThemes.forEach(theme => {
          const { area_id, area_name } = theme;

          if (!areasMap[area_id]) {
              areasMap[area_id] = {
                  id: area_id,
                  name: area_name,
                  themes: []
              };
          };

          const { id, name, count_activities_finished, count_activities, finished } = theme;
          areasMap[area_id].themes.push({
              id,
              name,
              count_activities_finished,
              count_activities,
              finished
          });
      });

      // Convertir el objeto de áreas en un array
      this.allDownloadedThemesGroupedByArea = Object.values(areasMap);
      console.log("getOfflineThemes raw Themes::",this.allDownloadedThemes);
      // console.log("getOfflineThemes groupByArea::", this.allDownloadedThemesGroupedByArea);

      return this.allDownloadedThemesGroupedByArea;
    }
    catch (error) {
      console.log("Error getOfflineThemes: " + JSON.stringify(error));
      return this.allDownloadedThemes;
    };
  }

  //*MAIN AUDITS
  async createMainAuditsTable(mainAuditsArray, checklistId, entityId) {
    console.log("createMainAuditsTable array recibido:",mainAuditsArray)
    if(!this.database) { await this.createSQLdb() };

    try {
      await this.database.executeSql(`CREATE TABLE IF NOT EXISTS main_audits (
        id_code text primary key,
        checklistId integer,
        entityId integer,

        activity_id integer,
        comments text,
        photo text,
        qualification_id integer,
        qualification_response text,
        principal_comment text,
        videos text
      )`,[]);

      const query = "INSERT OR REPLACE INTO main_audits VALUES ( ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)";
      const mainAuditsArrayVerified = mainAuditsArray && mainAuditsArray.length ? mainAuditsArray : []
      for(let audit of mainAuditsArrayVerified) {
        if(audit.photo?.length) {
          for (const photo of audit.photo){
           let imgPath = await this.downloadActivityMedia(photo, "photo");
           photo.url = imgPath;
          };
        };
        if(audit.videos?.length) {
          for (const video of audit.videos){
           let videoLocalPath = await this.downloadActivityMedia(video, "video");
           video.url = videoLocalPath;
          };
        };

        console.log("**downloadActivityMedia: con path desde dispositivo::: ",audit);

        await this.database.executeSql(query, [
          `${entityId}-${checklistId}-${audit.activity_id}`,
          checklistId,
          entityId,
          audit.activity_id,
          audit.comments ? JSON.stringify(audit.comments) :  JSON.stringify([]),
          audit.photo ? JSON.stringify(audit.photo) :  JSON.stringify([]),
          audit.qualification_id,
          audit.qualification_response,
          audit.comments?.length ?  JSON.stringify(audit.comments[0]) : "",
          audit.videos ? JSON.stringify(audit.videos) :  JSON.stringify([])
        ])

        console.log('INSERT main_audits table!')
      };

    } catch (error) {
      console.log('Error createMainAuditsTable or INSERT SQLite:', error);
       throw error
    };
  }

  async getOfflineMainAuditsByChecklistEntity(checklistId, entityId) {
    if(!this.database) {
      await this.createSQLdb();
    };

   this.allDownloadedMainAudits = [];

    try {
      const data = await this.database.executeSql("SELECT * FROM main_audits WHERE checklistId = ? AND entityId = ?", [checklistId, entityId]);
      if(data.rows.length > 0) {
        for(let i = 0; i < data.rows.length; i++) {
          this.allDownloadedMainAudits.push(data.rows.item(i));
        };
      };

      this.allDownloadedMainAudits.forEach( audit => {
        audit.comments = audit.comments ? JSON.parse(audit.comments) : '';
        audit.photo = audit.photo ? JSON.parse(audit.photo) : [];
        audit.videos = audit.videos ? JSON.parse(audit.videos) : [];
        audit.principal_comment = audit.principal_comment ? JSON.parse(audit.principal_comment) : "";
      });

      return this.allDownloadedMainAudits;
    }
    catch (error) {
      console.log("Error getOfflineMainAuditsByChecklistEntity: " + JSON.stringify(error));
      return this.allDownloadedMainAudits;
    };
  }

  setActivitiesAuditValues(rawActivities, audits) {
    const realArrayActivities = rawActivities.map(originalActivity => {
        const correspondingItem = audits.find(audit => audit.activity_id === originalActivity.id);
        if (correspondingItem) {
            return {
                ...originalActivity,
                comments: correspondingItem.comments,
                photo: correspondingItem.photo,
                videos: correspondingItem.videos,
                qualification_id: correspondingItem.qualification_id,
                qualification_response: correspondingItem.qualification_response,
                principal_comment: correspondingItem.principal_comment
            };
        }
        return originalActivity;
    });

    return realArrayActivities
  }

  async updateActivityProgressInLocalAudit(actionType, checklistId, entityId,
    activity_id, comments:string, photo:string, videos:string, principal_comment:string, qualification_id:number, qualification_response = "") {

  if (!this.database) { await this.createSQLdb() };

     console.log(
      "actionType:",actionType,
      "checklistId:",checklistId,
      "entityId",entityId,
      "activity_id",activity_id,
      "comments" ,comments,
      "photo",photo,
      "videos",videos,
      "principal_comment",principal_comment,
      "qualification_id",qualification_id,
      "qualification_response ",qualification_response,
    )

  try {
    const id_code = `${entityId}-${checklistId}-${activity_id}`;
    let existingRecord = []
    // Verificar si el registro existe
    const checkQuery = `SELECT * FROM main_audits WHERE id_code = ?`;
    const result = await this.database.executeSql(checkQuery, [id_code]);
    console.log(result)
    if(result.rows.length > 0) {
        for(let i = 0; i < result.rows.length; i++) {
          existingRecord.push(result.rows.item(i));
        };
    };

    console.log("recordExists :", existingRecord)
    const recordExists = existingRecord.length > 0;
    // const existingRecord = recordExists ? result[0].rows.item(0) : null;

    if(actionType === "qualification") {
      if (recordExists) {
        // Actualizar registro existente
        if(qualification_id) {
          const query = `UPDATE main_audits SET
          comments = ?,
          photo = ?,
          qualification_id = ?

          WHERE checklistId = ? AND entityId = ? AND activity_id = ?`;

          await this.database.executeSql(query, [comments, photo, qualification_id, checklistId, entityId, activity_id]);
          console.log('main_audits progress qualification_id updated successfully!');

        };
        if(qualification_response) {
          const query = `UPDATE main_audits SET
          qualification_response = ?,
          qualification_id = ?

          WHERE checklistId = ? AND entityId = ? AND activity_id = ?`;

          await this.database.executeSql(query, [qualification_response, 1, checklistId, entityId, activity_id]);
          console.log('main_audits progress qualification_response updated successfully!');
        };
        return true;

      } else {
        const query = "INSERT OR REPLACE INTO main_audits VALUES ( ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)";
        await this.database.executeSql(query, [
          id_code,
          checklistId,
          entityId,
          activity_id,
          comments || '[]',
          photo || '[]',
          qualification_response ? 1 : qualification_id,
          qualification_response || '',
          principal_comment || '',
          videos || '[]'
        ])
        console.log('main_audits progress qualification_id,qualification_response  inserted successfully!');
         return true;
      }
    }

    if(actionType === "photo") {
      if (recordExists) {
        // Actualizar solo photo
        const query = `UPDATE main_audits SET photo = ? WHERE id_code = ?`;
        await this.database.executeSql(query, [photo, id_code]);
        console.log('main_audits progress photo updated successfully!');
        return true;
      } else {
        const query = "INSERT OR REPLACE INTO main_audits VALUES ( ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)";
        await this.database.executeSql(query, [
          id_code,
          checklistId,
          entityId,
          activity_id,
          comments || '[]',
          photo,
          null,
          qualification_response || '',
          principal_comment || '',
          videos || '[]'
        ])
      }
      console.log('main_audits progress photo inserted successfully!');
      return true;
    }
    if(actionType === "video") {
      if (recordExists) {
        // Actualizar solo photo
        const query = `UPDATE main_audits SET videos = ? WHERE id_code = ?`;
        await this.database.executeSql(query, [videos, id_code]);
        console.log('main_audits progress videos updated successfully!');
        return true;
      } else {
        const query = "INSERT OR REPLACE INTO main_audits VALUES ( ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)";
        await this.database.executeSql(query, [
          id_code,
          checklistId,
          entityId,
          activity_id,
          comments || '[]',
          photo,
          null,
          qualification_response || '',
          principal_comment || '',
          videos || '[]'
        ])
      }
      console.log('main_audits progress videos inserted successfully!');
      return true;
    }
    if(actionType === "comments") {
      if (recordExists) {
        const query = `UPDATE main_audits SET comments = ?, principal_comment = ? WHERE checklistId = ? AND entityId = ? AND activity_id = ?`;
        await this.database.executeSql(query, [comments, principal_comment,  checklistId, entityId, activity_id]);
        console.log('main_audits progress comment updated successfully!');
        return true;
      } else {
        const query = "INSERT OR REPLACE INTO main_audits VALUES ( ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)";
        await this.database.executeSql(query, [
          id_code,
          checklistId,
          entityId,
          activity_id,
          comments,
          photo || '[]',
          null,
          qualification_response || '',
          principal_comment || '',
          videos || '[]'
        ])
        console.log('main_audits progress comments inserted successfully!');
        return true;
      }
    }

    if(actionType === "knockoutActivity") {
      if (recordExists) {
        // Actualizar solo qualification_id
        const query = `UPDATE main_audits SET qualification_id = ? WHERE id_code = ?`;
        await this.database.executeSql(query, [qualification_id, id_code]);
        console.log('main_audits progress qualification_id for knockoutActivity updated successfully!');

      } else {
        const query = "INSERT OR REPLACE INTO main_audits VALUES ( ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)";
        await this.database.executeSql(query, [
          id_code,
          checklistId,
          entityId,
          activity_id,
          comments  || '[]',
          photo || '[]',
          qualification_id,
          qualification_response || '',
          principal_comment || '',
          videos || '[]'
        ])
        console.log('main_audits progress qualification_id for knockoutActivity inserted successfully!');
        return true;
      }
    }

  } catch (error) {
    console.log('Error updating/inserting main_audits progress:', error);
    throw error;
  }
}

  async updateActivityWithknockoutInLocalAudit(qualification_id,checklistId, entityId, activity_id) {
    if (!this.database) { await this.createSQLdb() };

    try {
      const query = `UPDATE main_audits SET
      qualification_id = ?

      WHERE checklistId = ? AND entityId = ? AND activity_id = ?`;

      await this.database.executeSql(query, [ qualification_id, checklistId, entityId, activity_id]);
      console.log('main_audits progress qualification_id for knockoutActivity updated successfully!');
    } catch (error) {
      console.log('Error updating main_audits progress:', error);
      throw error;
    }
  }

  async countAllActivities(checklistId: number, entityId: number) {
    if (!this.database) { await this.createSQLdb() };

    try {
      // Consulta SQL para contar todos los registros que coinciden con los criterios
      const query = `SELECT COUNT(*) as total
                    FROM activities
                    WHERE checklistId = ?`;

      const result = await this.database.executeSql(query, [checklistId]);

      // Extraer el valor del contador del resultado
      const totalItems = result.rows.item(0).total;

      console.log(`Found ${totalItems} audit items for checklist ${checklistId} and entity ${entityId}`);
      return totalItems;
    } catch (error) {
      console.log('Error counting audit items:', error);
      throw error;
    }
  }

  async countActivitiesQualified(checklistId: number, entityId: number) {
    if (!this.database) { await this.createSQLdb() };

    try {
      const query = `SELECT COUNT(*) as count
                    FROM main_audits
                    WHERE checklistId = ?
                    AND entityId = ?
                    AND qualification_id IS NOT NULL
                    AND qualification_id != 0`;

      const result = await this.database.executeSql(query, [checklistId, entityId]);

      // Extrae el valor del contador del resultado
      const count = result.rows.item(0).count;

      console.log(`Found ${count} qualified audits for checklist ${checklistId} and entity ${entityId}`);
      return count;
    } catch (error) {
      console.log('Error counting qualified audits:', error);
      throw error;
    }
  }

  async getOfflineMainAuditById(checklistId, entityId, activityId) {
    const idCode = `${entityId}-${checklistId}-${activityId}`;
    if(!this.database) {
      await this.createSQLdb();
    };

   const activityAudit = [];

    try {
      const data = await this.database.executeSql("SELECT * FROM main_audits WHERE id_code = ?", [idCode]);
      if(data.rows.length > 0) {
        for(let i = 0; i < data.rows.length; i++) {
          activityAudit.push(data.rows.item(i));
        };
      };

      activityAudit.forEach( audit => {
        audit.comments = audit.comments ? JSON.parse(audit.comments) : [];
        audit.photo = audit.photo ? JSON.parse(audit.photo) : [];
        audit.videos = audit.videos ? JSON.parse(audit.videos) : [];
      });

      return activityAudit;
    }
    catch (error) {
      console.log("Error getOfflineMainAuditsByChecklistEntity: " + JSON.stringify(error));
      return activityAudit;
    };
  }

  //*Offline_audits por actividad (para sincronizar)
  async createOfflineAuditsToSyncActivitiesTable() {
    if(!this.database) {
			await this.createSQLdb();
		}
    try {
      await this.database.executeSql(`CREATE TABLE IF NOT EXISTS offline_audits (
        id text primary key,
        checklistId integer,
        entityId integer,
        activity_id integer,
        qualification_id integer,
        comments text,
        photos text,
        videos text,
        qualification_response text,
        action_type text,
        is_synced integer,
        date_audit text
        )`,[]);
        // knockout text,
        // url text,
        // method text,
			console.log("tabla offline_audits created");

    } catch (error) {
      console.log('Error createOfflineAuditsTable:', error);
      throw error
    };
  }

  async saveOfflineAuditToSyncActivities(auditOfflineDataToSave) {
    console.log("data recibida en saveOfflineAuditToSyncActivities:", auditOfflineDataToSave)
    const apiConnectors = this.api.getApiUrls;
		let query = "INSERT OR REPLACE INTO offline_audits VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)";
		this.database.executeSql(query, [
			auditOfflineDataToSave.auditID,
			auditOfflineDataToSave.checklistID,
			auditOfflineDataToSave.entityID,
			auditOfflineDataToSave.activityID,
			auditOfflineDataToSave.qualification_id ,
			auditOfflineDataToSave.comments ,
			auditOfflineDataToSave.photos ,
			auditOfflineDataToSave.videos ,
			auditOfflineDataToSave.qualification_response ,
			auditOfflineDataToSave.actionType,
			0,
      this.formatDate()
			// auditOfflineDataToSave.knockout ,
			// auditOfflineDataToSave.url ,
			// auditOfflineDataToSave.method ,
		] )
		.then(async () => {
			console.log('saveOfflineAudit insertado en SQLite', auditOfflineDataToSave.auditID);

      const queryC = `UPDATE offline_checklist_audits SET
      starts_at = ?,
      has_data_to_sync = ?,
      sync_status = ?

      WHERE checklistId = ? AND entityId = ?  AND (starts_at IS NULL OR starts_at = '')`;

      this.database.executeSql(queryC, [this.formatDate(),1, "pending", auditOfflineDataToSave.checklistID, auditOfflineDataToSave.entityID])
      .then(()=> {console.log('offline_checklist_audits starts_at updated successfully!');})
      .catch(()=> {console.log('offline_checklist_audits starts_at updated error!');});
      // console.log('offline_checklist_audits starts_at updated successfully!');


		})
		.catch(error => console.error('Error al insertar datos en SQLite saveOfflineAuditToSyncActivities:', error));
  }
  //! para testeo!
  async getOfflineAuditToSyncActivities(checklistId, entityId) {
    if(!this.database) {
      await this.createSQLdb();
    };

   let dataToSync = [];

    try {
      const data = await this.database.executeSql("SELECT * FROM offline_audits WHERE checklistId = ? AND entityId = ?", [checklistId, entityId]);
      if(data.rows.length > 0) {
        for(let i = 0; i < data.rows.length; i++) {
          dataToSync.push(data.rows.item(i));
        };
      };

      dataToSync.forEach( audit => {
        audit.comments = audit.comments ? JSON.parse(audit.comments) : '';
        audit.photos = audit.photos ? JSON.parse(audit.photos) : [];
        audit.videos = audit.videos ? JSON.parse(audit.videos) : [];
      });

      return dataToSync;
    }
    catch (error) {
      console.log("Error getOfflineAuditToSyncActivities: " + JSON.stringify(error));
      return dataToSync;
    };
  }

  //*Offline_audits por checklist/entidad (para sincronizar)
  async createOfflineAuditsToSyncChecklistTable(entitiesArray, checklistId ) {
    if(!this.database) {
			await this.createSQLdb();
		}
    try {
      await this.database.executeSql(`CREATE TABLE IF NOT EXISTS offline_checklist_audits (
        id text primary key,
        checklistId integer,
        entityId integer,

        knockout text,
        signature_supervisor text,
        signature_supervised text,
        action_plan text,
        has_data_to_sync integer,
        finished_order integer,
        is_synced integer,
        finishes_at text,
        starts_at text,
        sync_status text

      )`,[]);
			console.log("tabla offline_checklist_audits created");
      const query = "INSERT OR REPLACE INTO offline_checklist_audits VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ? )";
      for(let entity of entitiesArray) {
        await this.database.executeSql(query, [
          `${entity.id}-${checklistId}`,
          checklistId,
          entity.id,
          "",
          "",
          "",
          "",
          0,
          0,
          0,
          "",
          "",
          "pending"
        ])

        console.log('INSERT offline_checklist_audits table!')
      };

    } catch (error) {
      console.log('Error createOfflineAuditsToSyncChecklistTable:', error);
      throw error
    };
  }

  async updateOfflineAuditToSyncChecklist(actionType, auditOfflineDataToSave, checklistId, entityId) {
    console.log("data recibida en saveOfflineAuditToSyncChecklist:", auditOfflineDataToSave, checklistId, entityId)
    if (!this.database) { await this.createSQLdb() };
    try {
      if(actionType === "knockout") {
        const query = `UPDATE offline_checklist_audits SET
        knockout = ?,
        has_data_to_sync = ?,
        sync_status = ?

        WHERE checklistId = ? AND entityId = ?`;

        await this.database.executeSql(query, [JSON.stringify(auditOfflineDataToSave.knockoutData),1,"pending", checklistId, entityId]);
        console.log('offline_checklist_audits knockout updated successfully!');
        return true;

      };
      if(actionType === "signature_supervisor") {
        const query = `UPDATE offline_checklist_audits SET
        signature_supervisor = ?,
        has_data_to_sync = ?,
        sync_status = ?

        WHERE checklistId = ? AND entityId = ?`;

        await this.database.executeSql(query, [auditOfflineDataToSave.signature_supervisor, 1,"pending", checklistId, entityId]);
        console.log('offline_checklist_audits signature_supervisor updated successfully!');
        return true;
      };
      if(actionType === "signature_supervised") {
        const query = `UPDATE offline_checklist_audits SET
        signature_supervised = ?,
        has_data_to_sync = ?,
        sync_status = ?

        WHERE checklistId = ? AND entityId = ?`;

        await this.database.executeSql(query, [JSON.stringify(auditOfflineDataToSave), 1,"pending", checklistId, entityId]);
        console.log('offline_checklist_audits signature_supervised updated successfully! ',checklistId, entityId );
        return true;
      };
      if(actionType === "finished_order") {
        const query = `UPDATE offline_checklist_audits SET
        finished_order = ?,
        has_data_to_sync = ?,
        sync_status = ?

        WHERE checklistId = ? AND entityId = ?`;

        await this.database.executeSql(query, [1,1,"pending", checklistId, entityId]);
        console.log('offline_checklist_audits finished_order updated successfully!');
        return true;
      };
      if(actionType === "action_plan") {
        const query = `UPDATE offline_checklist_audits SET
        action_plan = ?,
        finished_order = ?,
        has_data_to_sync = ?,
        sync_status = ?

        WHERE checklistId = ? AND entityId = ?`;

        await this.database.executeSql(query, [auditOfflineDataToSave.action_plan, 1, 1,"pending", checklistId, entityId]);
        console.log('offline_checklist_audits finished_order updated successfully!');
        return true;
      };

    } catch (error) {
      console.log('Error updating offline_checklist_audits :', error);
      throw error;
    }

  }

  // Método para actualizar el estado de sincronización
async updateSyncStatus(checklistId: number, entityId: number, status: string) {
  try {
    await this.database.executeSql(
      `UPDATE offline_checklist_audits
       SET sync_status = ?
       WHERE checklistId = ? AND entityId = ?`,
      [status, checklistId, entityId]
    );
  } catch (error) {
    console.error('Error updating sync status of offline_checklist_audits:', error);
  }
}
async updateSyncStatusByUniqueEntityInChecklist(checklistId: number, status: string) {
  try {
    await this.database.executeSql(
      `UPDATE offline_checklist_audits
       SET sync_status = ?
       WHERE checklistId = ?`,
      [status, checklistId]
    );
  } catch (error) {
    console.error('Error updating sync status of UniqueEntity in offline_checklist_audits:', error);
  }
}

// Método para obtener el estado de sincronización
async getSyncStatus(checklistId: number, entityId: number): Promise<string> {
  try {
    const result = await this.database.executeSql(
      `SELECT sync_status
       FROM offline_checklist_audits
       WHERE checklistId = ? AND entityId = ?`,
      [checklistId, entityId]
    );

    if (result.rows.length > 0) {
      return result.rows.item(0).sync_status;
    }
    return 'not_found';
  } catch (error) {
    console.error('Error getting sync status:', error);
    return 'not_found';
  }
}
async getSyncStatusByUniqueEntityInChecklist(checklistId: number): Promise<string> {
  try {
    const result = await this.database.executeSql(
      `SELECT sync_status
       FROM offline_checklist_audits
       WHERE checklistId = ?`,
      [checklistId]
    );

    if (result.rows.length > 0) {
      return result.rows.item(0).sync_status;
    }
    return 'not_found';
  } catch (error) {
    console.error('Error getting sync status:', error);
    return 'not_found';
  }
}

  //*LISTA DE SUPERVISADOS
  async createSupervisedTables(supervisedArray, checklistId, entityId) {
    console.log("createSupervisedTables array recibido:",supervisedArray)
    if(!this.database) { await this.createSQLdb() };

    try {
      await this.database.executeSql(`CREATE TABLE IF NOT EXISTS supervised (
        id_code text primary key,
        checklistId integer,
        entityId integer,

        age integer,
        fullname text,
        id integer,
        lastname text,
        name text,
        surname text
      )`,[]);

      const query = "INSERT OR REPLACE INTO supervised VALUES ( ?, ?, ?, ?, ?, ?, ?, ?, ?)";

      for(let supervised of supervisedArray) {
        await this.database.executeSql(query, [
          `${entityId}-${checklistId}-${supervised.id}`,
          checklistId,
          entityId,
          supervised.age,
          supervised.fullname,
          supervised.id,
          supervised.lastname,
          supervised.name,
          supervised.surname
        ])

        console.log('INSERT supervised table!')
      };

    } catch (error) {
      console.log('Error createSupervisedTables or INSERT SQLite:', error);
       throw error
    };
  }

  async getOfflineSupervised(entityId, checklistId) {
    if(!this.database) {
      await this.createSQLdb();
    };

    let allSupervisedDownloaded = [];

     try {
      const data = await this.database.executeSql("SELECT * FROM supervised WHERE checklistId = ? AND entityId = ?", [checklistId, entityId]);
      if(data.rows.length > 0) {
        for(let i = 0; i < data.rows.length; i++) {
          allSupervisedDownloaded.push(data.rows.item(i));
        };
      };

      return allSupervisedDownloaded;
    }
    catch (error) {
      console.log("Error getOfflineSupervised: " + JSON.stringify(error));
      return allSupervisedDownloaded;
    };
  }

  //*OTHERS
  async downloadChecklistCover(checklist, ruta_imagen):Promise<string> {
		let url_media: string = ruta_imagen;
		let extension: string[] = url_media.split(".");
		let docName = `${checklist.id}.${extension[extension.length - 1]}` ;
		let localUrl = "";

		try {
			let filePath = this.file.dataDirectory +'/ch-covers/' + docName;
			let coverFolder = await this.checkCoversFolder();
			console.log('Directory ch-covers exists:',coverFolder);

			if(coverFolder) {
				const responseDownload = await this.nativeHTTP.downloadFile(url_media, {}, {}, filePath);
				localUrl = responseDownload.nativeURL;
				console.log('ch-cover Descargado en:', responseDownload.nativeURL);
			} else {
				let coverFolderCreated = await this.createCoversFolder();
        console.log('Directory ch-covers created:',coverFolderCreated);
				if(coverFolderCreated) {
					const responseDownload = await this.nativeHTTP.downloadFile(url_media, {}, {}, filePath);
					localUrl = responseDownload.nativeURL;
				  console.log('ch-cover Descargado en:', responseDownload.nativeURL);
				};
			};
			return localUrl;
		} catch (err) {
			console.log('error block ... ', err.status, JSON.stringify(err.error));
			return localUrl;
		}
	}


  private getFileExtensionFromUrl(url: string): string {
    try {
      // Remover parámetros de query (?param=value)
      const urlWithoutParams = url.split('?')[0];

      // Extraer la extensión
      const parts = urlWithoutParams.split('.');
      if (parts.length > 1) {
        const extension = parts[parts.length - 1].toLowerCase();
        // Validar que sea una extensión de imagen común
        const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'mp4', 'webm', 'mpeg', 'mov'];
        if (validExtensions.includes(extension)) {
          return extension;
        }
      }
      return null;
    } catch (error) {
      console.log('Error extracting extension:', error);
      return null;
    }
  }

   // Detecta el tipo de contenido realizando una petición HEAD

  private async detectContentType(url: string): Promise<string> {
    try {
      // Realizar petición HEAD para obtener headers sin descargar el contenido
      const response = await this.nativeHTTP.sendRequest(url, {
        method: 'head',
        serializer: 'json'
      });

      const contentType = response.headers['content-type'] || response.headers['Content-Type'];

      if (contentType) {
        // Mapear content-type a extensión
        const typeMap = {
          'image/jpeg': 'jpg',
          'image/jpg': 'jpg',
          'image/png': 'png',
          'image/gif': 'gif',
          'image/webp': 'webp',
          'image/bmp': 'bmp',
          'image/svg+xml': 'svg'
        };

        return typeMap[contentType.toLowerCase()] || 'jpg'; // Default a jpg
      }

      return 'jpg'; // Default fallback
    } catch (error) {
      console.log('Error detecting content type:', error);
      return 'jpg'; // Default fallback
    }
  }

 private async detectVideoContentType(url: string): Promise<string> {
  try {
    // Realizar petición HEAD para obtener headers sin descargar el contenido
    const response = await this.nativeHTTP.sendRequest(url, {
      method: 'head',
      serializer: 'json'
    });

    const contentType = response.headers['content-type'] || response.headers['Content-Type'];

    if (contentType) {
      // Mapear content-type a extensión de video
      const videoTypeMap = {
        'video/mp4': 'mp4',
        'video/webm': 'webm',
        'video/mpeg': 'mpeg',
        'video/quicktime': 'mov'
        // 'video/x-msvideo': 'avi',
        // 'video/x-ms-wmv': 'wmv',
        // 'video/ogg': 'ogv',
        // 'video/3gpp': '3gp',
        // 'video/x-flv': 'flv',
        // 'video/x-matroska': 'mkv'
      };

      return videoTypeMap[contentType.toLowerCase()] || 'mp4'; // Default a mp4
    }

    return 'mp4'; // Default fallback
  } catch (error) {
    console.log('Error detecting video content type:', error);
    return 'mp4'; // Default fallback
  }
 }

  async downloadActivityMedia(media, type): Promise<string> {
    //  for (const photo of photos) {
       let url_media: string = media.url;
       let localUrl = "";

       try {
         // Intentar obtener extensión de la URL
         let extension = this.getFileExtensionFromUrl(url_media);

         // Si no se puede obtener la extensión de la URL, detectar por content-type
         if (!extension) {
           console.log('No extension found in URL, detecting content type...');
           if(type === "photo") {
             extension = await this.detectContentType(url_media);
            } else {
              extension = await this.detectVideoContentType(url_media);
            };
         }

         let docName = `${media.media_id}.${extension}`;
         let filePath;
        if(type === "photo") {
          filePath = this.file.dataDirectory + '/ch-online-img-audit/' + docName;
          // Verificar/crear carpeta
          let coverFolder = await this.checkOnlineImgFolder();
          console.log('Directory ch-online-img-audit exists:', coverFolder);

          if (!coverFolder) {
            let coverFolderCreated = await this.createOnlineImgFolder();
            console.log('Directory ch-online-img-audit created:', coverFolderCreated);
            if (!coverFolderCreated) {
              throw new Error('Could not create ch-online-img-audit directory');
            }
          };

        } else {
          filePath = this.file.dataDirectory + '/ch-online-video-audit/' + docName;
          // Verificar/crear carpeta
          let videoFolder = await this.checkOnlineVideoFolder();
          console.log('Directory ch-online-video-audit exists:', videoFolder);

          if (!videoFolder) {
            let videoFolderCreated = await this.createOnlineVideoFolder();
            console.log('Directory ch-online-video-audit created:', videoFolderCreated);
            if (!videoFolderCreated) {
              throw new Error('Could not create ch-online-video-audit directory');
            }
          };
        };

        // Descargar archivo
         const responseDownload = await this.nativeHTTP.downloadFile(url_media, {}, {}, filePath);
         localUrl = responseDownload.nativeURL;
         console.log('ch-online-img-audit or ch-online-video-audit Descargado en:', responseDownload.nativeURL);

         return localUrl;

       } catch (err) {
         console.log('error block ... ', err.status, JSON.stringify(err.error));
         return localUrl;
       }
    //  }
  }

  async checkCoversFolder():Promise<boolean> {
		try {
			await this.file.checkDir(this.file.dataDirectory, 'ch-covers');
			return true;
		} catch (error) {
			return false;
		}
	}

  async createCoversFolder():Promise<boolean> {
		try {
			await this.file.createDir(this.file.dataDirectory, 'ch-covers', false);
			return true;
		} catch (error) {
			return false;
		}
	}
  async checkOnlineImgFolder():Promise<boolean> {
		try {
			await this.file.checkDir(this.file.dataDirectory, 'ch-online-img-audit');
			return true;
		} catch (error) {
			return false;
		}
	}

  async createOnlineImgFolder():Promise<boolean> {
		try {
			await this.file.createDir(this.file.dataDirectory, 'ch-online-img-audit', false);
			return true;
		} catch (error) {
			return false;
		}
	}

  async checkOnlineVideoFolder():Promise<boolean> {
		try {
			await this.file.checkDir(this.file.dataDirectory, 'ch-online-video-audit');
			return true;
		} catch (error) {
			return false;
		}
	}

  async createOnlineVideoFolder():Promise<boolean> {
		try {
			await this.file.createDir(this.file.dataDirectory, 'ch-online-video-audit', false);
			return true;
		} catch (error) {
			return false;
		}
	}
  //delete methods
  async deleteChecklist(checklistId: number) {
    try {
      await this.database.executeSql('DELETE FROM checklists WHERE id = ?', [checklistId]);
       console.log('checklists eliminado correctamente')
    } catch (error) {
      console.error('Error al eliminar el checklists:', error)
    }
	}
  async deleteChecklistEntities(checklistId: number) {
    try {
      await this.database.executeSql('DELETE FROM entities WHERE checklistId = ?', [checklistId]);
       console.log('entities eliminado correctamente')
    } catch (error) {
      console.error('Error al eliminar el entities:', error)
    }
	}
  async deleteChecklistActivities(checklistId: number) {
    try {
      await this.database.executeSql('DELETE FROM activities WHERE checklistId = ?', [checklistId]);
       console.log('activities eliminado correctamente')
    } catch (error) {
      console.error('Error al eliminar el activities :', error)
    }
	}
  async deleteChecklistThemes(checklistId: number) {
    try {
      await this.database.executeSql('DELETE FROM themes WHERE checklistId = ?', [checklistId]);
       console.log('themes eliminado correctamente')
    } catch (error) {
      console.error('Error al eliminar el themes :', error)
    }
	}
  async deleteChecklistMainAudits(checklistId: number) {
    try {
      await this.database.executeSql('DELETE FROM main_audits WHERE checklistId = ?', [checklistId]);
       console.log('main_audits eliminado correctamente')
    } catch (error) {
      console.error('Error al eliminar el main_audits :', error)
    }
	}
  async deleteChecklistOfflineAudits(checklistId: number) {
    try {
      await this.database.executeSql('DELETE FROM offline_audits WHERE checklistId = ?', [checklistId]);
       console.log('offline_audits eliminado correctamente')
    } catch (error) {
      console.error('Error al eliminar el offline_audits :', error)
    }
	}
  async deleteChecklistSupervised(checklistId: number) {
    try {
      await this.database.executeSql('DELETE FROM supervised WHERE checklistId = ?', [checklistId]);
       console.log('supervised eliminado correctamente')
    } catch (error) {
      console.error('Error al eliminar el supervised :', error)
    }
	}

  async getOfflineChecklistIds() {
    if(!this.database) {
			await this.createSQLdb();
		};
		if(this.database) {
      try {
        const data = await this.database.executeSql("SELECT id FROM checklists", []);
        this.downloadedChecklistID = [];
				// let res = [];
				if(data.rows.length > 0) {
					for(var i = 0; i < data.rows.length; i++) {
						this.downloadedChecklistID.push(data.rows.item(i).id);
					};
					console.log("hay IDS checklists offline!", this.downloadedChecklistID);
				}
      } catch (error) {
        this.downloadedChecklistID = [];
        console.log("Error get IDs de checklists offline: " + JSON.stringify(error));
      };

		} else {
			console.log("no DB for IDs de checklists");
			this.downloadedChecklistID = [];
		}
	}

  async getTotalPhotosAndComments(checklistId, entityId) {
    let totalPhotos = 0;
    let totalComments = 0;
    let totalVideos = 0;
    try {
      // Consulta para obtener todos los registros que coinciden con checklistId y entityId
      const query = "SELECT photo, comments, videos FROM main_audits WHERE checklistId = ? AND entityId = ?";
      const result = await this.database.executeSql(query, [checklistId, entityId]);



      // Itera sobre los resultados y suma los totales
      if (result && result.rows && result.rows.length > 0) {
        for (let i = 0; i < result.rows.length; i++) {
          const row = result.rows.item(i);
          console.log("getTotalPhotosAndComments main_audits:: ", row)
          // Procesa las fotos si existen
          if (row.photo) {
            try {
              const photosArray = JSON.parse(row.photo);
              totalPhotos += Array.isArray(photosArray) ? photosArray.length : 0;
            } catch (error) {
              console.error('Error al parsear JSON de fotos:', error);
            }
          }

          // Procesa los comentarios si existen
          if (row.comments) {
            try {
              const commentsArray = JSON.parse(row.comments);
              totalComments += Array.isArray(commentsArray) ? commentsArray.length : 0;
            } catch (error) {
              console.error('Error al parsear JSON de comentarios:', error);
            }
          }
          // Procesa los videos si existen
          if (row.videos) {
            try {
              const videosArray = JSON.parse(row.videos);
              totalVideos += Array.isArray(videosArray) ? videosArray.length : 0;
            } catch (error) {
              console.error('Error al parsear JSON de videos:', error);
            }
          }
        }
      }

      return {
        totalPhotos,
        totalComments,
        totalVideos
      };
    } catch (error) {
      console.error('Error en getTotalPhotosAndComments:', error);
      // throw error;
      return {
        totalPhotos,
        totalComments,
        totalVideos
      };
    }
  }
  // async getTotalPhotosAndComments(checklistId, entityId) {
  //   let totalPhotos = 0;
  //   let totalComments = 0;
  //   try {
  //     // Consulta para obtener todos los registros que coinciden con checklistId y entityId
  //     const query = "SELECT photos, comments FROM offline_audits WHERE checklistId = ? AND entityId = ?";
  //     const result = await this.database.executeSql(query, [checklistId, entityId]);



  //     // Itera sobre los resultados y suma los totales
  //     if (result && result.rows && result.rows.length > 0) {
  //       for (let i = 0; i < result.rows.length; i++) {
  //         const row = result.rows.item(i);

  //         // Procesa las fotos si existen
  //         if (row.photos) {
  //           try {
  //             const photosArray = JSON.parse(row.photos);
  //             totalPhotos += Array.isArray(photosArray) ? photosArray.length : 0;
  //           } catch (error) {
  //             console.error('Error al parsear JSON de fotos:', error);
  //           }
  //         }

  //         // Procesa los comentarios si existen
  //         if (row.comments) {
  //           try {
  //             const commentsArray = JSON.parse(row.comments);
  //             totalComments += Array.isArray(commentsArray) ? commentsArray.length : 0;
  //           } catch (error) {
  //             console.error('Error al parsear JSON de comentarios:', error);
  //           }
  //         }
  //       }
  //     }

  //     return {
  //       totalPhotos,
  //       totalComments
  //     };
  //   } catch (error) {
  //     console.error('Error en getTotalPhotosAndComments:', error);
  //     // throw error;
  //     return {
  //       totalPhotos,
  //       totalComments
  //     };
  //   }
  // }

  formatDate(timeInMilliseconds = null) {
    let date = timeInMilliseconds ? new Date(timeInMilliseconds) : new Date();
    // console.log("original date:",date)
    const padZero = (num) => num.toString().padStart(2, '0');
    const year = date.getFullYear().toString();
    const month = padZero(date.getMonth() + 1);
    const day = padZero(date.getDate());
    const hours = padZero(date.getHours());
    const minutes = padZero(date.getMinutes());
    const seconds = padZero(date.getSeconds());

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`; // 2024-04-13 14:34:54
  }





  // *pendientes por ENTIDAD
  // data_inverso text, // header con color y score de ch. inverso (no disponible en modo offline?)

}
